import { assert } from '../app/logger.js';
import { window } from '../window.js';
import { HTMLControl } from './html_control.js';

/**
 * Spreadsheet control.
 */
class SpreadsheetControl extends HTMLControl {
  get values() {
    let values = [];
    let rowValues;
    let columnCount = this.columnCount;
    for (let index = 0; index < this.cellCount; index++) {
      if (index % columnCount == 0) {
        values.push((rowValues = []));
      }
      rowValues.push(this.cellAtIndex(index).value);
    }

    // Pop the last item created for the last empty row.
    values.pop();
    return values;
  }

  appendRow(values) {
    return this.insertRow(this.rowCount - 1, values);
  }

  insertRow(index, values) {
    // Undefined index is a special value indicating that an empty row should
    // be appended.
    let beforeCell = index !== undefined ? this.firstCellOfRowAt(index) : null;
    this.insertBefore(this.columnsFragment.cloneNode(true), beforeCell);
    if (beforeCell && values) {
      for (
        let cell = beforeCell.previousElementSibling, i = values.length - 1;
        cell && i >= 0;
        cell = cell.previousElementSibling, i--
      ) {
        cell.value = values[i];
      }
    }

    let rowIndex = index !== undefined ? index : this.rowCount - 1;
    for (let columnIndex = 0; columnIndex < this.columnCount; columnIndex++) {
      this.dispatchEvent(
        new window.CustomEvent('cellinsert', {
          detail: {
            rowIndex,
            columnIndex,
          },
        })
      );
    }

    return rowIndex;
  }

  deleteRow(index) {
    let columnCount = this.columnCount;
    let startIndex = index * columnCount;
    let endIndex = startIndex + columnCount;
    this.#deleteCells(startIndex, endIndex, window.document.activeElement);
  }

  changeRow(index, values) {
    let firstCell = this.firstCellOfRowAt(index);
    assert(firstCell, `[spreadsheet-control] no cells at the ${index} row`);
    if (firstCell) {
      let firstCellIndex = this.cellIndexOf(firstCell);
      for (let i = 0; i < values.length; i++) {
        let cell = this.cellAtIndex(firstCellIndex + i);
        assert(
          cell,
          `[spreadsheet-control] no cell at the ${index} row at ${i} column`
        );
        if (cell) {
          cell.value = values[i];
        }
      }
    }
  }

  initialize() {
    super.initialize();

    this.addEventListener('input', ev => this.#appendRowOnChange(ev));
    this.addEventListener('valuechange', ev => this.#appendRowOnChange(ev));

    this.addEventListener(
      'blur',
      ev => {
        let el = ev.target;
        if (el.value != '') {
          return;
        }

        // Do not remove the last row.
        let columnCount = this.columnCount;
        while (el && el.parentNode != this) {
          el = el.parentNode;
        }
        assert(el, `[spreadsheet-control] no cell for blur target`);
        let cellIndex = this.cellIndexOf(el);
        assert(
          cellIndex != -1,
          `[spreadsheet-control] no cell index for blur target`
        );
        if (cellIndex == -1 || cellIndex + columnCount >= this.cellCount) {
          return;
        }

        // If a row is empty and not focused then remove it.
        let startIndex = cellIndex - (cellIndex % columnCount);
        let endIndex = startIndex + columnCount;
        for (let index = startIndex; index < endIndex; index++) {
          let cell = this.cellAtIndex(index);
          assert(
            cell,
            `[spreadsheet-control] no cell at ${index} index on ${cellIndex} cell blur`
          );
          if (cell.value != '' || ev.relatedTarget == cell) {
            return;
          }
        }
        this.#deleteCells(startIndex, endIndex, ev.relatedTarget);

        this.dispatchEvent(
          new window.CustomEvent('rowdelete', {
            detail: {
              rowIndex: Math.floor(cellIndex / columnCount),
            },
            bubbles: true,
          })
        );
      },
      {
        capture: true,
      }
    );

    this.addEventListener('change', ev => {
      let el = ev.target;
      this.dispatchEvent(
        new window.CustomEvent('rowchange', {
          detail: {
            rowIndex: this.rowIndexOf(el),
            columnIndex: this.columnIndexOf(el),
            value: el.value,
          },
          bubbles: true,
        })
      );
    });

    this.insertRow();
  }

  get columnCount() {
    return this.columnsFragment.children.length;
  }

  get cellCount() {
    return this.children.length - 1;
  }

  get rowCount() {
    return this.cellCount / this.columnCount;
  }

  firstCellOfRowAt(index) {
    return this.children[index * this.columnCount + 1];
  }

  cellAt(rowIndex, columnIndex) {
    return this.children[rowIndex * this.columnCount + columnIndex + 1];
  }

  cellAtIndex(index) {
    return this.children[index + 1];
  }

  cellIndexOf(cell) {
    return Array.from(this.children).indexOf(cell) - 1;
  }

  rowIndexOf(cell) {
    let cellIndex = this.cellIndexOf(cell);
    return Math.floor(cellIndex / this.columnCount);
  }

  columnIndexOf(cell) {
    let cellIndex = this.cellIndexOf(cell);
    return cellIndex % this.columnCount;
  }

  get cells() {
    return Array.from(this.children).slice(1);
  }

  get columnsFragment() {
    return this.querySelector('template').content;
  }

  #appendRowOnChange(ev) {
    let el = ev.target;
    if (el.value != '') {
      while (el && el.parentNode != this) {
        el = el.parentNode;
      }
      let length = el.parentNode.children.length;
      let index = Array.from(el.parentNode.children).indexOf(el);
      if (index + this.columnCount >= length) {
        this.insertRow();
        this.dispatchEvent(
          new window.CustomEvent('rowappend', {
            detail: {
              rowIndex: this.rowIndexOf(el),
            },
            bubbles: true,
          })
        );
        return;
      }
    }
  }

  #deleteCells(startIndex, endIndex, focusCell) {
    let redirectFocus = false;
    for (let index = startIndex; index < endIndex; index++) {
      let cell = this.cellAtIndex(startIndex);
      if (focusCell == cell) {
        redirectFocus = true;
      }
      cell.remove();
    }

    if (redirectFocus) {
      let focusIndex = startIndex > 0 ? startIndex - 1 : startIndex;
      this.cellAtIndex(focusIndex).focus();
    }
  }
}

window.customElements.define('spreadsheet-control', SpreadsheetControl);

export { SpreadsheetControl };
